import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Radio, Toast } from 'antd-mobile-v5'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSelector, useDispatch } from 'react-redux'
import RenewStatements from './RenewStatements'
import { useHistory } from 'react-router-dom'
import { showToast } from '../common/tools'
import { useRenewalCloseOrder, useRenewalPaymentStatus } from '../../services/hooks'
import { createWposPayTypePicker, isValidWposQrContent, pickFuiouChannel } from '../FormTools/utils'
import { getMemberRenewFuiouOrder } from '../../reducers/selectors'
import { hideFuiouRenewQr } from '../../actions'
import FuiouQrPopup from '../FormTools/FuiouQrPopup'
import styles from './Renew.module.css'

const MemberRenew = ({ memberRenewList, submitMemberRenew }) => {
  const [selectedEfr, setSelectedEfr] = useState(false)
  const [agreement, setAgreement] = useState(false)
  const [showQr, setShowQr] = useState(false)
  const [qrUrl, setQrUrl] = useState('')
  // WPOS 渠道选择结果（WECHAT/ALIPAY），传给 FuiouQrPopup 展示对应渠道边框色与文案
  const [qrPayType, setQrPayType] = useState(null)
  const { push } = useHistory()
  const { formatMessage } = useIntl()
  const dispatch = useDispatch()
  const fuiouOrder = useSelector(getMemberRenewFuiouOrder)
  const wposPickerRef = useRef(null)
  // 互斥锁：picker await 期间防重复下单（submit-btn 是 div，无法用 disabled）
  const payingRef = useRef(false)
  // await picker 后组件可能已卸载：setState 前判 mounted 防卸载告警
  const mountedRef = useRef(true)

  // 列表加载后若默认值不匹配任何选项（如仅含 EFRI 无核心档），重置为第一项，避免提交错误的 withEfri
  useEffect(() => {
    const dtos = memberRenewList && memberRenewList.renewalMemberDTOS
    if (!dtos || !dtos.length) return
    if (!dtos.some((item) => item.isEfr === selectedEfr)) {
      setSelectedEfr(dtos[0].isEfr)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberRenewList])

  useEffect(() => {
    document.body.style.setProperty('background-color', '#ffffff', 'important')

    return () => {
      document.body.style.setProperty('background-color', '#f5f5f9', 'important')
    }
  }, [])

  // WPOS（非微信 UA）：侦听 Fuiou order 成功响应 → 显示 QR 弹窗。
  // 单码通道返回渠道二维码内容（qr.alipay.com / weixin://），只做本地编码展示，
  // 校验用二维码内容白名单（兼容历史富友聚合链接形态）
  useEffect(() => {
    if (!fuiouOrder || !fuiouOrder.url || pickFuiouChannel() !== 'WPOS') return
    if (!isValidWposQrContent(fuiouOrder.url)) {
      dispatch(hideFuiouRenewQr())
      Toast.show({ icon: 'fail', content: formatMessage({ id: 'fuiou-pay-url-invalid' }) })
      return
    }
    if (!fuiouOrder.orderId) {
      // orderId 缺失无法轮询支付结果，二维码会永挂零反馈：提示失败并复位，避免用户干等
      dispatch(hideFuiouRenewQr())
      Toast.show({ icon: 'fail', content: formatMessage({ id: 'fuiou-pay-fail' }) })
      return
    }
    setQrUrl(fuiouOrder.url)
    setShowQr(true)
  }, [fuiouOrder, dispatch, formatMessage])

  // 离开页面时清理 Redux 中残留的 Fuiou 订单，避免再次进入时重弹旧二维码
  useEffect(() => {
    return () => {
      dispatch(hideFuiouRenewQr())
    }
  }, [dispatch])

  // 组件卸载时清理残留的命令式渠道选择面板
  useEffect(() => {
    // StrictMode 下 effect 会 setup→cleanup→setup 重跑：cleanup 置 false 后必须复位，
    // 否则重挂载后 mountedRef 恒 false，支付回调全部失效
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (wposPickerRef.current) {
        wposPickerRef.current.close()
        wposPickerRef.current = null
      }
    }
  }, [])

  const handleCloseQr = useCallback(() => {
    setShowQr(false)
    dispatch(hideFuiouRenewQr())
  }, [dispatch])

  // WPOS 扫码后按订单维度轮询支付结果（/api/renewal/paymentStatus，paid === true 即支付完成）
  const { runAsync: checkRenewalStatus } = useRenewalPaymentStatus({ manual: true, onError: () => true })
  const checkStatus = useCallback(async () => {
    // orderId 缺失时不发起请求，避免无参轮询打后端
    const orderId = fuiouOrder && fuiouOrder.orderId
    if (!orderId) return false
    const res = await checkRenewalStatus({ orderId })
    return !!(res && res.paid)
  }, [checkRenewalStatus, fuiouOrder])

  // 关单只由用户显式「取消订单」触发（弹窗关闭/超时都不再关单，见 FuiouQrPopup）。
  // 续费 closeOrder body 为 { orderId }（字符串，非教材单的 { id }）；失败向上抛，由弹窗静默收起——
  // 后端幂等，失败时旧单仍是未支付，重试会复用同一笔建单（findReusableRenewalOrder），不会重复扣款
  const { runAsync: closeRenewalOrder } = useRenewalCloseOrder({ manual: true, onError: () => true })
  const cancelOrder = useCallback(async () => {
    const orderId = fuiouOrder && fuiouOrder.orderId
    if (!orderId) return
    await closeRenewalOrder(orderId)
  }, [closeRenewalOrder, fuiouOrder])

  const handlePaymentSuccess = useCallback(() => {
    setShowQr(false)
    dispatch(hideFuiouRenewQr())
    // Toast 消失后再刷新，避免成功提示被整页刷新瞬间冲掉（对齐 EcardCheckoutPopup/CoursePicOrder）
    Toast.show({
      icon: 'success',
      content: formatMessage({ id: 'fuiou-pay-success' }),
      afterClose: () => window.location.reload(),
    })
  }, [dispatch, formatMessage])

  const filterIsEfr = () => {
    if (selectedEfr) {
      return memberRenewList.renewalMemberDTOS.find((item) => item.isEfr)
    }
    return {}
  }

  const submitRenewAction = async () => {
    if (payingRef.current) return
    if (!agreement) {
      showToast(formatMessage({ id: 'common-check-agreement' }), false)
      return
    }
    payingRef.current = true
    try {
      // PC 扫码（WPOS）：先弹微信/支付宝渠道选择（单码扫码，FAPPLET 已下线），再带 payType 下单
      if (pickFuiouChannel() === 'WPOS') {
        const picker = createWposPayTypePicker(formatMessage)
        wposPickerRef.current = picker
        const payType = await picker.promise
        wposPickerRef.current = null
        // 用户取消渠道选择，静默返回
        if (!payType) return
        if (mountedRef.current) setQrPayType(payType)
        submitMemberRenew(selectedEfr, payType)
        return
      }
      // selectedEfr 即用户所选项的 isEfr：true=核心+EFRI，false=仅核心。
      // 据此传 withEfri，让后端按所选定价/收款（与结算单展示一致）。
      submitMemberRenew(selectedEfr)
    } finally {
      payingRef.current = false
    }
  }

  const handleEfrOptionChange = (val) => {
    setSelectedEfr(val)
  }

  return (
    <>
      <FuiouQrPopup
        visible={showQr}
        url={qrUrl}
        // CHNA-2618：下单响应新增 price/currency（恒等于实扣金额），以最新响应为准
        price={fuiouOrder && fuiouOrder.price}
        currency={fuiouOrder && fuiouOrder.currency}
        onClose={handleCloseQr}
        onPaymentSuccess={handlePaymentSuccess}
        checkStatus={checkStatus}
        payType={qrPayType}
        titleId="fuiou-qr-auto-renew-title"
        // 续费保持原 5 分钟支付时限与超时文案
        timeoutMs={5 * 60 * 1000}
        timeoutMessageId="fuiou-pay-timeout"
        onCancelOrder={cancelOrder}
      />
      {JSON.stringify(memberRenewList) !== '{}' && (
        <div className={styles.renew}>
          <div className={styles.header}>
            <img src={require('../../assets/images/logo/padi-logo-dark.png')} className={styles['padi-logo']} alt="" />
          </div>
          <div className={styles.content}>
            <h2 className={styles.title}>
              <FormattedMessage id="renew-title" />
            </h2>
            <Radio.Group onChange={handleEfrOptionChange} value={selectedEfr}>
              {memberRenewList.renewalMemberDTOS.map((item) => (
                <React.Fragment key={item.isEfr ? 'efr' : 'core'}>
                  <div className={styles.options}>
                    <Radio className={styles.option} value={item.isEfr}>
                      {item.isEfr ? `${memberRenewList.title} + EFRI` : memberRenewList.title}
                    </Radio>
                  </div>
                </React.Fragment>
              ))}
            </Radio.Group>

            <RenewStatements
              memberRenewList={memberRenewList}
              selectedEfr={filterIsEfr()}
              setAgreement={setAgreement}
              agreement={agreement}
            />
            <div className={styles.footer}>
              <div
                className={styles['back-btn']}
                role="button"
                tabIndex={0}
                onClick={() => push('/myProfile')}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    push('/myProfile')
                  }
                }}
              >
                <FormattedMessage id="renew-back" />
              </div>
              <div
                className={styles['submit-btn']}
                role="button"
                tabIndex={0}
                onClick={() => submitRenewAction()}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    submitRenewAction()
                  }
                }}
              >
                <FormattedMessage id="common-confirmRequest" />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default MemberRenew
